import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, ShieldCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import { z } from "zod";
import { Brand } from "../../components/Brand";
import { useAuth } from "./auth-context";

const loginSchema = z.object({ email: z.string().email(), password: z.string().min(10), full_name: z.string().min(2).optional() });
type Form = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { user, login, register: createAccount } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Form>({ resolver: zodResolver(loginSchema), defaultValues: { email: "", password: "" } });
  if (user) return <Navigate to="/" replace />;
  const submit = handleSubmit(async (data) => {
    setSubmitError("");
    try {
      if (mode === "login") await login(data);
      else await createAccount({ email: data.email, password: data.password, full_name: data.full_name || "Rihvo traveller" });
    } catch (error) { setSubmitError(error instanceof Error ? error.message : "We couldn't sign you in"); }
  });
  return <main className="auth-page">
    <section className="auth-visual">
      <img src="/images/japan-journey-hero.png" alt="A quiet path beneath vermilion gates in autumn Japan" />
      <div className="auth-visual-shade" />
      <div className="auth-brand"><Brand /></div>
      <motion.div className="auth-story" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .2 }}>
        <span className="story-kicker"><Sparkles size={15} />Your whole journey, held beautifully</span>
        <h1>More present.<br />Less preoccupied.</h1>
        <p>Rihvo.fun remembers the details, protects the plan, and gives every day room to unfold.</p>
      </motion.div>
      <div className="photo-credit">KYOTO · JAPAN</div>
    </section>
    <section className="auth-form-panel">
      <div className="auth-mobile-brand"><Brand /></div>
      <motion.div className="auth-form-wrap" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="eyebrow">{mode === "login" ? "WELCOME BACK" : "BEGIN YOUR JOURNEY"}</p>
        <h2>{mode === "login" ? "Your journeys are waiting." : "Travel starts long before departure."}</h2>
        <p className="auth-subtitle">{mode === "login" ? "Sign in to continue planning beautifully." : "Create one private home for every trip, booking, and memory."}</p>
        <div className="segmented"><button className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Sign in</button><button className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>Create account</button></div>
        <form onSubmit={submit}>
          {mode === "register" && <label>Your name<input autoComplete="name" placeholder="How should we greet you?" {...register("full_name")} /></label>}
          <label>Email address<input type="email" autoComplete="email" placeholder="you@example.com" {...register("email")} />{errors.email && <small>{errors.email.message}</small>}</label>
          <div className="form-field"><label htmlFor="voyage-password">Password</label><span className="password-field"><input id="voyage-password" type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="At least 10 characters" {...register("password")} /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span>{errors.password && <small>{errors.password.message}</small>}</div>
          {submitError && <p className="form-error" role="alert">{submitError}</p>}
          <button className="button primary auth-submit" disabled={isSubmitting}>{isSubmitting ? "Opening your travel world…" : mode === "login" ? "Continue to Rihvo.fun" : "Create my travel home"}<ArrowRight size={18} /></button>
        </form>
        <div className="privacy-note"><ShieldCheck size={17} /><span><strong>Your travel life stays yours.</strong> Self-hosted, private, and never sold.</span></div>
      </motion.div>
    </section>
  </main>;
}
